import reactUpdate from 'immutability-helper';
import React, {useEffect, useState} from 'react';

import isArchived from 'archive/isArchived';
import FieldTable, {ClientSchemaField} from 'registry/field/FieldTable';
import {CONFIRM_DELETE_ALL} from 'registry/field/FieldTableConstants';
import StorableFieldTableRow from 'registry/field/StorableFieldTableRow';
import * as RegistryUtil from 'registry/RegistryUtil';
import {CreateLocationSchemaParams, UpdateLocationSchemaParams} from 'samples/SampleSchemaActions';
import {LocationSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import SchemaDescriptionInput from 'schemas/SchemaDescriptionInput';
import SchemaWarehouseNameField from 'schemas/SchemaWarehouseNameField';
import {
  StorableSchemaSettingProps,
  StorableSchemaSettingsQueryingProps as QueryingProps,
  useQueryingStorableSchemaSettings,
} from 'settings/samples/StorableSchemaSettingsConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import SettingsField from 'ui-library/SettingsField';
import * as modals from 'util/Modals';
import {ElementMouseEvent, MakeFieldsOptional} from 'util/Types';

export interface LocationSchemaSettingsProps extends StorableSchemaSettingProps {
  locationSchema?: LocationSchema;
  onCreate: (locationSchema: CreateLocationSchemaParams) => void;
  onUpdate: (locationSchema: UpdateLocationSchemaParams) => void;
}

type ClientLocationSchema = MakeFieldsOptional<
  Omit<LocationSchema, 'allFields'>,
  'id' | 'fields' | 'registryId' | 'iconId'
> & {
  allFields: Array<SchemaField | ClientSchemaField>;
};

function LocationSchemaSettings(props: LocationSchemaSettingsProps & QueryingProps): JSX.Element {
  const [locationSchema, setLocationSchema] = useState<ClientLocationSchema>(
    props.locationSchema ?? {
      name: '',
      prefix: '',
      sqlIdentifier: '',
      schemaDescription: '',
      allFields: [],
    }
  );

  const isWritable = (): boolean =>
    props.isWritable && !isArchived({...locationSchema, id: locationSchema.id ?? ''});

  useEffect(() => {
    if (props.locationSchema) {
      setLocationSchema(props.locationSchema);
    }
  }, [props.locationSchema]);

  const handleChangeSchema = (update: Partial<ClientLocationSchema>): void => {
    const updatedLocationSchema = reactUpdate(locationSchema, {$merge: update});
    setLocationSchema(updatedLocationSchema);
    if (!props.isBeingCreated) {
      handleSubmit(null, updatedLocationSchema);
    }
  };

  const handleSubmit = (e: ElementMouseEvent | null, locationSchemaToSubmit?: ClientLocationSchema): void => {
    locationSchemaToSubmit = locationSchemaToSubmit ?? locationSchema;
    const fields = RegistryUtil.sanitizeFields(locationSchemaToSubmit.allFields);
    const parsedLocationSchema = {
      ...locationSchemaToSubmit,
      allFields: fields,
      fields,
    };
    if (props.isBeingCreated) {
      props.onCreate(parsedLocationSchema);
      return;
    }
    props.onUpdate(parsedLocationSchema);
  };

  const handleChangePrefix = (prefix: string): void => {
    handleChangeSchema({prefix});
  };

  const handleChangeName = (name: string): void => {
    handleChangeSchema({name});
  };

  const handleChangeSqlIdentifier = (sqlIdentifier: string): void => {
    handleChangeSchema({sqlIdentifier});
  };

  const handleChangeSchemaDescription = (schemaDescription: string): void => {
    handleChangeSchema({schemaDescription});
  };

  const handleChangeFields = (allFields: Array<ClientSchemaField>): void => {
    handleChangeSchema({allFields});
  };

  const confirmDeleteFields = (onSubmit: (param?: string) => void): void => {
    modals
      .confirm(
        'Confirm Delete',
        `Are you sure you want to delete this location field?
      All values corresponding to this location field will be deleted. This action cannot be undone.`,
        'danger'
      )
      .done(() => {
        onSubmit(CONFIRM_DELETE_ALL);
      });
  };

  return (
    <div className="u-flex-grow">
      <div className="settingsPageContainer-section">
        <Row>
          <Col sm={props.locationSchema ? 4 : 6}>
            <SettingsField
              isBeingCreated={props.isBeingCreated}
              isRequired={true}
              isWritable={isWritable()}
              label="Prefix"
              name="prefix"
              onChange={handleChangePrefix}
              value={locationSchema.prefix}
            />
          </Col>
          <Col sm={props.locationSchema ? 4 : 6}>
            <SettingsField
              isBeingCreated={props.isBeingCreated}
              isRequired={true}
              isWritable={isWritable()}
              label="Name"
              name="name"
              onChange={handleChangeName}
              value={locationSchema.name}
            />
          </Col>
          {props.locationSchema && (
            <Col sm={4}>
              <SchemaWarehouseNameField
                onChange={handleChangeSqlIdentifier}
                isWritable={isWritable() && !props.isBeingCreated}
                sqlIdentifier={locationSchema.sqlIdentifier}
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col sm={12}>
            <SchemaDescriptionInput
              value={locationSchema.schemaDescription ?? null}
              onChange={handleChangeSchemaDescription}
              isWritable={isWritable()}
              isBeingCreated={props.isBeingCreated}
            />
          </Col>
        </Row>
      </div>
      <div className="settingsPageContainer-section">
        <Row>
          <Col sm={12}>
            <FieldTable // TODO (BNCH-53233): Render FieldTableLoading while fetching data
              shouldShowIsParentLink={false}
              shouldShowIsRequired={true}
              shouldShowIsIntegration={false}
              shouldShowTooltipText={true}
              schemaId={props.locationSchema ? props.locationSchema.id : null}
              schema={props.locationSchema}
              fields={locationSchema.allFields}
              handleDeleteConfirmation={confirmDeleteFields}
              isBeingCreated={props.isBeingCreated}
              isDisabled={!isWritable()}
              itemClass={StorableFieldTableRow}
              onFieldsChange={handleChangeFields}
              registryId={props.registryId}
              ownerUrlId={props.ownerUrlId}
              ownerId={props.registry.ownerJson.id}
              savingState={props.savingState}
              schemaType="location"
              storableSchemas={props.storableSchemas}
            />
          </Col>
        </Row>
      </div>
      {props.isBeingCreated && (
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          disabled={!isWritable()}
          onClick={handleSubmit}
          size={ButtonSize.MEDIUM}
          text="Create"
        />
      )}
    </div>
  );
}

export default function QueryingLocationSchemaSettings(
  props: LocationSchemaSettingsProps
): JSX.Element | null {
  return useQueryingStorableSchemaSettings(props, (queryingProps) => (
    <LocationSchemaSettings {...props} {...queryingProps} />
  ));
}
